import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { DataList } from '../data-list/data-list.class';
import { EnumDataListColumnAlign, EnumDataListColumnType, IDataListBtnOption, IDataListColumn, IDataListOrderBy, DataListComponent } from '../data-list/data-list.component';
import { Subscription } from 'rxjs';
import { IUser } from '@simplyfly/shared';
import { AppDataService } from 'src/app/services/data/app-data.service';
import { IInvoice } from '@simplyfly/shared';
import { getInvoiceDaysLate } from 'src/app/interfaces/invoice.helpers';
import { InvoiceService } from 'src/app/services/invoices/invoices.service';
import { UserService } from 'src/app/services/user/user.service';
import { EnumAcl } from '@simplyfly/shared';
import { AclService } from 'src/app/services/acl.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { EnumInvoiceType } from '@simplyfly/shared';
import { getEnumInvoiceTypeLabel } from 'src/app/enums/invoice-type.helpers';
import { Router, RouterLink } from '@angular/router';
import { faArrowRight, faHand, faPlus } from '@fortawesome/free-solid-svg-icons';
import { formatPrice } from 'src/app/misc.utils';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

@Component({
    selector: 'app-invoices-list-for-enquiry',
    templateUrl: './invoices-list-for-enquiry.component.html',
    styleUrls: ['./invoices-list-for-enquiry.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DataListComponent, RouterLink, FaIconComponent, TranslateModule]
})
export class InvoicesListForEnquiryComponent
  extends DataList<IInvoice>
  implements OnInit, OnChanges, OnDestroy
{
  @Input() enquiryId: string | null = null;
  @ViewChild('modalInvoiceRequest', { static: false }) modalInvoiceRequestElement: ElementRef;

  EnumAcl = EnumAcl;
  EnumInvoiceType = EnumInvoiceType;

  formatPrice = formatPrice;

  faPlus = faPlus;
  faArrowRight = faArrowRight;

  usersObj: { [id: string]: IUser } = {};

  currentPagination = 'enquiry-invoices-list';

  columns: IDataListColumn[] = [];

  orderBy: IDataListOrderBy | null = { field: 'created', direction: 'desc' };

  isLogged = false;

  addInvoiceDropdownOptions: IDataListBtnOption[] = [];

  requestedInvoiceType: EnumInvoiceType | null = null;
  invoicesOptionsForRequest: IInvoice[] = [];

  private subscriptions = new Subscription();

  constructor(
    private authService: AuthService,
    private invoiceService: InvoiceService,
    private appDataService: AppDataService,
    private userService: UserService,
    private aclService: AclService,
    private translateService: TranslateService,
    private router: Router
  ) {
    super();

    this.setDataService(this.invoiceService);
  }

  ngOnInit(): void {
    this.columns = [
      {
        title: this.translateService.instant('INVOICE.REFERENCE'),
        field: 'ref',
        notSortable: true
      },
      {
        title: this.translateService.instant('GENERAL.TYPE'),
        field: 'invoiceType',
        notSortable: true,
        type: EnumDataListColumnType.function,
        resolve: (invoice: IInvoice): string => {
          return this.translateService.instant(getEnumInvoiceTypeLabel(invoice.invoiceType));
        }
      },
      {
        title: this.translateService.instant('INVOICE.ISSUE_DATE'),
        field: 'issueDate',
        notSortable: true,
        type: EnumDataListColumnType.date
      },
      {
        title: this.translateService.instant('INVOICE.DUE_DATE'),
        field: 'dueDate',
        notSortable: true,
        type: EnumDataListColumnType.date
      },
      {
        title: this.translateService.instant('INVOICE.IS_LATE'),
        field: 'late',
        notSortable: true,
        type: EnumDataListColumnType.function,
        resolve: (invoice: IInvoice): string => {
          const daysLate: number = getInvoiceDaysLate(invoice);

          let html = '';

          if (invoice.isFullyPaid) {
            html =
              '<span class="badge bg-success">' +
              this.translateService.instant('INVOICE.PAID') +
              '</span>';
          } else {
            if (daysLate) {
              html +=
                '<span class="badge bg-danger">' +
                this.translateService.instant('GENERAL.X_DAYS').replace('{{number}}', daysLate) +
                '<span>';
            } else {
              html =
                '<span class="badge bg-success">' +
                this.translateService.instant('GENERAL.NO') +
                '</span>';
            }
          }

          return html;
        }
      },
      {
        title: this.translateService.instant('INVOICE.COMPANY'),
        field: 'clientBillingInfos.clientName',
        notSortable: true,
        type: EnumDataListColumnType.function,
        resolve: (invoice: IInvoice): string => {
          return invoice.supplierBillingInfos?.name || invoice.clientBillingInfos?.clientName || '';
        }
      },
      {
        title: this.translateService.instant('INVOICE.LABEL'),
        field: 'invoiceObject',
        notSortable: true
      },
      {
        title: this.translateService.instant('GENERAL.IS_SENT'),
        field: 'sentToClient',
        notSortable: true,
        type: EnumDataListColumnType.boolean
      },
      {
        title: this.translateService.instant('INVOICE.AMOUNT_HT'),
        field: 'amountHtTotal',
        notSortable: true,
        type: EnumDataListColumnType.price,
        align: EnumDataListColumnAlign.end
      },
      {
        title: this.translateService.instant('INVOICE.AMOUNT_TVA'),
        field: 'amountTvaTotal',
        notSortable: true,
        type: EnumDataListColumnType.price,
        align: EnumDataListColumnAlign.end
      },
      {
        title: this.translateService.instant('INVOICE.AMOUNT_TTC'),
        field: 'amountTtcTotal',
        notSortable: true,
        type: EnumDataListColumnType.price,
        align: EnumDataListColumnAlign.end
      },
      {
        title: this.translateService.instant('INVOICE.PAYMENTS_DATE'),
        field: 'soldedDate',
        notSortable: true
      },
      {
        title: this.translateService.instant('BANK_ACCOUNTS.SINGULAR'),
        field: 'bankAccount',
        notSortable: true,
        type: EnumDataListColumnType.function,
        resolve: (invoice: IInvoice): string => {
          return invoice.bankAccountData?.accountLabel || '';
        }
      },
      {
        title: this.translateService.instant('INVOICE.AMOUNT_PAID'),
        field: 'amountSoldePaid',
        notSortable: true,
        type: EnumDataListColumnType.price,
        align: EnumDataListColumnAlign.end
      },
      {
        title: this.translateService.instant('INVOICE.AMOUNT_LEFT'),
        field: 'amountSoldeLeft',
        notSortable: true,
        type: EnumDataListColumnType.price,
        align: EnumDataListColumnAlign.end
      },
      {
        title: this.translateService.instant('INVOICE.COMMENT'),
        field: 'comment',
        notSortable: true
      },
      {
        title: this.translateService.instant('GENERAL.DATE_CREATED'),
        field: 'created',
        notSortable: true,
        type: EnumDataListColumnType.date
      },
      {
        title: this.translateService.instant('GENERAL.CREATED_BY'),
        field: 'createdBy',
        notSortable: true,
        type: EnumDataListColumnType.function,
        resolve: (item: IInvoice): string => {
          return item.createdBy && this.usersObj[item.createdBy]
            ? this.usersObj[item.createdBy].initials
            : '';
        }
      }
    ];

    this.setUpAddInvoiceDropdownOptions();

    this.authService.isLoggedObservable.subscribe(
      (isLogged: boolean) => (this.isLogged = isLogged)
    );

    this.updatedEnquiryId();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.enquiryId && !changes.enquiryId.isFirstChange()) {
      this.updatedEnquiryId();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setUpAddInvoiceDropdownOptions(): void {
    this.addInvoiceDropdownOptions = [];

    if (this.hasAclAccess(EnumAcl.invoicesAddProforma)) {
      this.addInvoiceDropdownOptions.push({
        label: this.translateService.instant('INVOICE.NEW_PROFORMA'),
        icon: faPlus,
        action: () => {
          this.addInvoice(EnumInvoiceType.proforma);
        }
      });
    }

    if (this.hasAclAccess(EnumAcl.invoicesRequestFinalInvoice)) {
      this.addInvoiceDropdownOptions.push({
        label: this.translateService.instant('INVOICE.REQUEST_DEFINITIVE'),
        icon: faHand,
        action: () => {
          this.requestInvoice(EnumInvoiceType.definitive);
        }
      });
    }

    if (this.hasAclAccess(EnumAcl.invoicesAddFinalInvoice)) {
      this.addInvoiceDropdownOptions.push({
        label: this.translateService.instant('INVOICE.NEW_DEFINITIVE'),
        icon: faPlus,
        action: () => {
          this.addInvoice(EnumInvoiceType.definitive);
        }
      });
    }

    if (this.hasAclAccess(EnumAcl.invoicesRequestFinalCreditNoteInvoice)) {
      this.addInvoiceDropdownOptions.push({
        label: this.translateService.instant('INVOICE.REQUEST_CREDIT_NOTE'),
        icon: faHand,
        action: () => {
          this.requestInvoice(EnumInvoiceType.creditNote);
        }
      });
    }

    if (this.hasAclAccess(EnumAcl.invoicesAddCreditNoteProforma)) {
      this.addInvoiceDropdownOptions.push({
        label: this.translateService.instant('INVOICE.NEW_CREDIT_NOTE_PROFORMA'),
        icon: faPlus,
        action: () => {
          this.addInvoice(EnumInvoiceType.proformaCreditNote);
        }
      });
    }

    if (this.hasAclAccess(EnumAcl.invoicesAddCreditNoteFinal)) {
      this.addInvoiceDropdownOptions.push({
        label: this.translateService.instant('INVOICE.NEW_CREDIT_NOTE'),
        icon: faPlus,
        action: () => {
          this.addInvoice(EnumInvoiceType.creditNote);
        }
      });
    }

    if (this.hasAclAccess(EnumAcl.invoicesAddPurchaseInvoice)) {
      this.addInvoiceDropdownOptions.push({
        label: this.translateService.instant('INVOICE.NEW_PURCHASE'),
        icon: faPlus,
        action: () => {
          this.addInvoice(EnumInvoiceType.purchase);
        }
      });
      this.addInvoiceDropdownOptions.push({
        label: this.translateService.instant('INVOICE.NEW_PURCHASE_PROFORMA'),
        icon: faPlus,
        action: () => this.addInvoice(EnumInvoiceType.purchaseProforma)
      });
    }

    if (this.hasAclAccess(EnumAcl.invoicesAddPurchaseCreditNote)) {
      this.addInvoiceDropdownOptions.push({
        label: this.translateService.instant('INVOICE.NEW_PURCHASE_CREDIT_NOTE'),
        icon: faPlus,
        action: () => {
          this.addInvoice(EnumInvoiceType.purchaseCreditNote);
        }
      });
      this.addInvoiceDropdownOptions.push({
        label: this.translateService.instant('INVOICE.NEW_PURCHASE_PROFORMA_CREDIT_NOTE'),
        icon: faPlus,
        action: () => this.addInvoice(EnumInvoiceType.purchaseProformaCreditNote)
      });
    }
  }

  updatedEnquiryId(): void {
    if (this.isLogged) {
      if (this.enquiryId) {
        this.conditions = [
          {
            field: 'enquiryId',
            operator: '==',
            value: this.enquiryId
          }
        ];

        this.resetAndLoad();
      }

      this.loadUsers();
    } else {
      setTimeout(() => {
        this.updatedEnquiryId();
      }, 500);
    }
  }

  loadUsers(): void {
    this.subscriptions.add(
      this.userService.getAll().subscribe((users: IUser[]) => {
        for (const user of users) {
          this.usersObj[user.id] = user;
        }
      })
    );
  }

  hasAclAccess(id: EnumAcl): boolean {
    return this.aclService.hasAclAccess(id);
  }

  async addInvoice(invoiceType: EnumInvoiceType): Promise<void> {
    if (this.enquiryId) {
      let countInvoicesSameType = 0;

      for (const invoice of this.data) {
        if (invoice.invoiceType === invoiceType) {
          countInvoicesSameType++;
        }
      }

      if (!countInvoicesSameType) {
        this.redirectToAddUrl(invoiceType);
      } else {
        if (confirm(this.translateService.instant('INVOICE.ALREADY_EXIST_INVOICE_SAME_TYPE'))) {
          this.redirectToAddUrl(invoiceType);
        }
      }
    } else {
      this.redirectToAddUrl(invoiceType);
    }
  }

  redirectToAddUrl(invoiceType: EnumInvoiceType): void {
    let url: string | null = null;

    switch (invoiceType) {
      case EnumInvoiceType.proforma:
        url = '/admin/invoices/add-proforma';
        break;
      case EnumInvoiceType.proformaCreditNote:
        url = '/admin/invoices/add-credit-note-proforma';
        break;
      case EnumInvoiceType.creditNote:
        url = '/admin/invoices/add-credit-note-invoice';
        break;
      case EnumInvoiceType.definitive:
        url = '/admin/invoices/add-final-invoice';
        break;
      case EnumInvoiceType.purchase:
        url = '/admin/invoices/add-purchase-invoice';
        break;
      case EnumInvoiceType.purchaseCreditNote:
        url = '/admin/invoices/add-purchase-credit-note';
        break;
      case EnumInvoiceType.purchaseProforma:
        url = '/admin/invoices/add-purchase-proforma';
        break;
      case EnumInvoiceType.purchaseProformaCreditNote:
        url = '/admin/invoices/add-purchase-proforma-credit-note';
        break;
    }

    if (this.enquiryId) {
      url += '/' + this.enquiryId;
    }

    if (url) {
      this.router.navigateByUrl(url);
    }
  }

  requestInvoice(invoiceType: EnumInvoiceType): void {
    this.requestedInvoiceType = invoiceType;
    this.invoicesOptionsForRequest = [];

    if (this.enquiryId) {
      for (const invoice of this.data) {
        switch (this.requestedInvoiceType) {
          case EnumInvoiceType.definitive:
            if (invoice.invoiceType === EnumInvoiceType.proforma) {
              this.invoicesOptionsForRequest.push(invoice);
            }
            break;
          case EnumInvoiceType.creditNote:
            if (invoice.invoiceType === EnumInvoiceType.proformaCreditNote) {
              this.invoicesOptionsForRequest.push(invoice);
            }
            break;
        }
      }

      if (this.invoicesOptionsForRequest.length) {
        if (this.modalInvoiceRequestElement.nativeElement) {
          window.$(this.modalInvoiceRequestElement.nativeElement).modal('show');
        }
      } else {
        this.requestInvoiceFromZero(this.requestedInvoiceType);
      }
    } else {
      this.redirectToRequestUrl(invoiceType);
    }
  }

  requestInvoiceFromZero(invoiceType: EnumInvoiceType): void {
    let countInvoicesSameType = 0;

    for (const invoice of this.data) {
      if (invoice.invoiceType === invoiceType) {
        countInvoicesSameType++;
      }
    }

    if (!countInvoicesSameType) {
      this.redirectToRequestUrl(invoiceType);
    } else {
      if (confirm(this.translateService.instant('INVOICE.ALREADY_EXIST_INVOICE_SAME_TYPE'))) {
        this.redirectToRequestUrl(invoiceType);
      }
    }
  }

  redirectToRequestUrl(invoiceType: EnumInvoiceType): void {
    let url: string | null = null;

    switch (invoiceType) {
      case EnumInvoiceType.creditNote:
        url = '/admin/invoices/request-credit-note-invoice';
        break;
      case EnumInvoiceType.definitive:
        url = '/admin/invoices/request-final-invoice';
        break;
    }

    if (this.enquiryId) {
      url += '/enquiry/' + this.enquiryId;
    }

    if (url) {
      this.router.navigateByUrl(url);
    }
  }
}
